import { z } from 'zod';
import { MemoryTypeSchema } from './agent-memory.js';
import { AgentKindSchema } from './agent.js';
/**
 * Per-target auto-approve policy (DEV-16823) — the opt-in generalization of the
 * memory approval state machine. An agent-authored memory normally enters
 * PENDING and waits for human review; when a matching ENABLED policy exists for
 * its target (+ optional type), the write choke point (`memory-write.ts`)
 * creates it APPROVED instead. Default is unchanged: NO policy ⇒ nothing
 * auto-approves. Only agent-authored PENDING is eligible — feedback-loop
 * CANDIDATE proposals stay governed by the DEV-16831 regression gate (D3).
 *
 * `targetKind` picks the applicability axis (mirrors the memory binding axes);
 * exactly the columns for that axis are set (a DB CHECK + the create superRefine
 * enforce the shape). `memoryType = null` means the policy covers every type.
 */
export const AutoApproveTargetKindSchema = z.enum([
    'AGENT',
    'COLLECTION',
    'NODE',
    'ALL',
    'AGENT_KIND',
]);
// ─── Entity ───────────────────────────────────────────────────────────────────
export const AgentMemoryAutoApprovePolicySchema = z.object({
    id: z.string(),
    orgId: z.string(),
    targetKind: AutoApproveTargetKindSchema,
    /** Set iff targetKind = AGENT (may be a global managed system agent). */
    agentId: z.string().nullable(),
    /**
     * Set iff targetKind = AGENT_KIND (DEV-17256, §4.7): covers every agent of
     * this kind. Reach stays bounded by the self-scope predicate in
     * resolveAutoApprove — ORCHESTRATION saves must be self-scoped (bindings
     * reach nobody but the author), CHAT saves must carry a userSubject.
     */
    agentKind: AgentKindSchema.or(z.literal('LEGACY')).nullable(),
    /** Set iff targetKind = COLLECTION. */
    collectionId: z.string().nullable(),
    /** Both set iff targetKind = NODE. */
    workflowId: z.string().nullable(),
    nodeKey: z.string().nullable(),
    /** null = every memory type; else restrict auto-approve to this type. */
    memoryType: MemoryTypeSchema.nullable(),
    enabled: z.boolean(),
    createdBySubject: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// ─── Create ─────────────────────────────────────────────────────────────────
/**
 * Enforces the same target-shape as the DB CHECK so a bad target is a clean 400
 * (not a raw constraint error): exactly the columns for `targetKind` are given.
 */
export const CreateAutoApprovePolicyInputSchema = z
    .object({
    targetKind: AutoApproveTargetKindSchema,
    agentId: z.string().optional(),
    /**
     * Only the two populations §4.7 analyzed are creatable: ORCHESTRATION
     * (self-scoped fleet learning) and CHAT (user-scoped preferences).
     * REFINEMENT/LEGACY have no bounded-reach story and are rejected.
     */
    agentKind: z.enum(['ORCHESTRATION', 'CHAT']).optional(),
    collectionId: z.string().optional(),
    workflowId: z.string().optional(),
    nodeKey: z.string().optional(),
    /** Omit / null = auto-approve every memory type for this target. */
    memoryType: MemoryTypeSchema.nullish(),
    enabled: z.boolean().default(true),
})
    .superRefine((v, ctx) => {
    const forbid = (field) => {
        if (v[field] !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field} must be omitted when targetKind is ${v.targetKind}`,
                path: [field],
            });
        }
    };
    const require = (field) => {
        if (v[field] === undefined || v[field] === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field} is required when targetKind is ${v.targetKind}`,
                path: [field],
            });
        }
    };
    switch (v.targetKind) {
        case 'AGENT':
            require('agentId');
            forbid('agentKind');
            forbid('collectionId');
            forbid('workflowId');
            forbid('nodeKey');
            break;
        case 'AGENT_KIND':
            require('agentKind');
            forbid('agentId');
            forbid('collectionId');
            forbid('workflowId');
            forbid('nodeKey');
            break;
        case 'COLLECTION':
            require('collectionId');
            forbid('agentId');
            forbid('agentKind');
            forbid('workflowId');
            forbid('nodeKey');
            break;
        case 'NODE':
            require('workflowId');
            require('nodeKey');
            forbid('agentId');
            forbid('agentKind');
            forbid('collectionId');
            break;
        case 'ALL':
            forbid('agentId');
            forbid('agentKind');
            forbid('collectionId');
            forbid('workflowId');
            forbid('nodeKey');
            break;
    }
});
// ─── Update ─────────────────────────────────────────────────────────────────
/**
 * Only `enabled` is mutable — the target + type are the policy's identity (and
 * its unique key), so a different target/type is a new policy, not an edit.
 */
export const UpdateAutoApprovePolicyInputSchema = z.object({
    enabled: z.boolean(),
});
//# sourceMappingURL=auto-approve-policy.js.map